import "../../modules/crm/jobs/components/JobInvoiceSendDrawer.css";
import PhoneNumberSelect from "../form/PhoneNumberSelect";
import { useEffect, useRef, useState } from "react";
import { Drawer, DrawerRef } from "../drawers/Drawer";
import { useHub } from "../../core/Global";
import { API_ROUTES } from "../../core/Constants";
import { FontAwesomeIcons } from "../../core/Types";
import PhoneItem from "../../models/PhoneItem";
import { toastSuccess } from "../base/Toast";
import Icon from "../base/Icon";
import Button from "../base/Button";
import Checkbox from "../form/Checkbox";
import Tagify from "../tagify/Tagify";
import TextArea from "../form/TextArea";
import PhoneInput from "../form/PhoneInput";
import FlexRow from "../base/FlexRow";


export interface ExportColumn {
    key: string
    label: string
    default?: boolean
}

type SendChannel = "download" | "email" | "phone" | "wa";

interface IProps {
    apiRoute: string
    title: string
    objectName: string
    columns: ExportColumn[]
    // Current filters/keyword so the backend re-queries the same visible set.
    browse?: any
    // When set, only these rows are exported (selected-rows export).
    selectedIds?: string[]
    // Source for the SMS sender numbers dropdown. Defaults to the account's own numbers.
    senderNumbersUrl?: string
    onClose: () => void
}

const DRAWER_ID = "drawer-datatable-export";

const CHANNELS: { key: SendChannel; label: string; icon: FontAwesomeIcons; iconType?: "solid" | "brands" }[] = [
    { key: "download", label: "Download", icon: "arrow-down-to-line" },
    { key: "email", label: "Email", icon: "envelope" },
    { key: "phone", label: "SMS", icon: "comment-sms" },
    { key: "wa", label: "WhatsApp", icon: "whatsapp", iconType: "brands" }
];

const FORMATS: { key: string; label: string }[] = [
    { key: "pdf", label: "PDF" },
    { key: "excel", label: "EXCEL" },
    { key: "csv", label: "CSV" }
];

export default function ExportDrawer(props: IProps) {

    const hub = useHub();
    const drawerRef = useRef<DrawerRef>(null);

    const [sendTo, sendToSet] = useState<SendChannel>("download");

    const [formats, formatsSet] = useState<string[]>(["pdf"]);
    const [columns, columnsSet] = useState<string[]>(
        props.columns.filter(c => c.default !== false).map(c => c.key)
    );

    const [recipients, recipientsSet] = useState<string[]>();
    const [phone, phoneSet] = useState<PhoneItem>();
    const [senderLineId, senderLineIdSet] = useState<string>();
    const [message, messageSet] = useState<string>();

    const [sending, sendingSet] = useState<boolean>(false);
    const [downloading, downloadingSet] = useState<boolean>(false);

    useEffect(() => { window.hideFormError("format"); }, [formats]);
    useEffect(() => { window.hideFormError("columns"); }, [columns]);

    const toggleFormat = (key: string, on: boolean) =>
        formatsSet(f => on ? [...f.filter(x => x !== key), key] : f.filter(x => x !== key));

    const toggleColumn = (key: string, on: boolean) =>
        columnsSet(c => on ? [...c.filter(x => x !== key), key] : c.filter(x => x !== key));

    const validate = (forSend: boolean): boolean => {
        window.hideFormErrors();
        let valid = true;

        if (formats.length === 0) {
            window.formError("format", "Please select at least 1 export format!", DRAWER_ID);
            valid = false;
        }

        if (columns.length === 0) {
            window.formError("columns", "Please select at least 1 column!", DRAWER_ID);
            valid = false;
        }

        if (forSend && sendTo === "email" && (!recipients || recipients.length < 1)) {
            window.formError("recipients", "Please choose at least 1 recipient", DRAWER_ID);
            valid = false;
        }

        if (forSend && (sendTo === "phone" || sendTo === "wa")) {
            const digits = (phone?.phone ?? "").replace(/\D/g, "");
            if (digits.length < 9) {
                window.formError("phone", "Please write a valid phone number!", DRAWER_ID);
                valid = false;
            }
        }

        if (forSend && sendTo === "phone" && window.isNullOrEmpty(senderLineId)) {
            window.formError("senderLineId", "Please select a sender number!", DRAWER_ID);
            valid = false;
        }

        return valid;
    };

    // The current filters/keyword are sent as query params (same encoding the list
    // uses) so the backend re-queries exactly the visible set. The export config
    // goes in the body.
    const buildFilterQuery = (): string => {
        const b = props.browse;
        let q = "";
        if (b?.keyword) q += "keyword=" + encodeURIComponent(b.keyword);
        (b?.filterParams ?? []).forEach((p: any) => {
            if (q !== "") q += "&";
            const ext = p.op === "not-equal" ? "Not" : "";
            q += p.name + ext + "=" + encodeURIComponent(p.value);
        });
        return q ? "?" + q : "";
    };

    // Columns + formats are ordered to match the on-screen column order.
    const buildPayload = (channel: SendChannel) => {
        const orderedColumns = props.columns.filter(c => columns.includes(c.key)).map(c => c.key);
        return {
            ids: props.selectedIds,
            columns: orderedColumns,
            formats,
            sendTo: channel,
            recipients,
            phone: phone?.phone,
            phoneCountryCode: phone?.countryCode,
            senderLineId,
            message
        };
    };

    const download = () => {
        if (!validate(false)) return;
        downloadingSet(true);
        hub.Post(props.apiRoute + "export" + buildFilterQuery(), buildPayload("download"), res => {
            downloadingSet(false);
            if (res.success) {
                (res.data?.files ?? []).forEach((f: any) => { if (f?.url) window.open(f.url, "_blank"); });
                drawerRef.current?.close();
            }
        });
    };

    const send = () => {
        if (!validate(true)) return;
        sendingSet(true);
        hub.Post(props.apiRoute + "export" + buildFilterQuery(), buildPayload(sendTo), res => {
            sendingSet(false);
            if (res.success) {
                toastSuccess(sendTo === "email"
                    ? `${props.objectName} export has been sent successfully!`
                    : `${props.objectName} export link has been sent successfully!`);
                drawerRef.current?.close();
            }
        });
    };

    const onlySelected = (props.selectedIds?.length ?? 0) > 0;

    return (
        <Drawer ref={drawerRef} id={DRAWER_ID} size="medium"
            title={onlySelected ? `Export ${props.selectedIds!.length} Selected` : `Export ${props.title}`}
            onClose={props.onClose}>
            <div className="pt-3">

                <div className="fw-bold fs-7 mb-2">Deliver via</div>
                <div className="d-flex flex-wrap mb-6">
                    {
                        CHANNELS.map(ch =>
                            <label key={ch.key}
                                className={"jis-channel flex-fill d-flex align-items-center justify-content-center me-3 mb-2 " + (sendTo === ch.key ? "active" : "")}>
                                <input type="radio" className="d-none" checked={sendTo === ch.key}
                                    onChange={e => { if (e.target.checked) sendToSet(ch.key); }} />
                                <Icon icon={ch.icon} type={ch.iconType} size={16} className="me-2" />
                                <span className="fs-7 fw-bold">{ch.label}</span>
                            </label>
                        )
                    }
                </div>

                <FlexRow className="mb-3">
                    <div className="fs-6 fw-bold">File Format</div>
                    <div id="format-err" className="small text-danger mx-3 form-error"></div>
                </FlexRow>
                <FlexRow className="mb-6 flex-wrap">
                    {
                        FORMATS.map(f =>
                            <Checkbox key={f.key} label={f.label} className="me-5"
                                checked={formats.includes(f.key)}
                                onChange={e => toggleFormat(f.key, e)} />
                        )
                    }
                </FlexRow>

                <FlexRow className="mb-3">
                    <div className="fs-6 fw-bold">Columns</div>
                    <div id="columns-err" className="small text-danger mx-3 form-error"></div>
                </FlexRow>
                <div className="mb-6 row">
                    {
                        props.columns.map(c =>
                            <div key={c.key} className="col-6 mb-3">
                                <Checkbox label={c.label}
                                    checked={columns.includes(c.key)}
                                    onChange={e => toggleColumn(c.key, e)} />
                            </div>
                        )
                    }
                </div>

                {
                    sendTo === "email" &&
                    <Tagify id="tagify-export-emails" block freeTags email closeOnSelect
                        label="Send to" labelUp apiUrl={API_ROUTES.TagifyEmailsAutocomplete}
                        onChange={e => recipientsSet(e)} />
                }

                {
                    sendTo === "phone" &&
                    <>
                        <PhoneNumberSelect name="senderLineId" label="Send from Number" labelUp block
                            apiUrl={props.senderNumbersUrl}
                            value={senderLineId} onChange={senderLineIdSet} />
                        <PhoneInput id="txt-export-sms" key="txt-export-sms" name="phone" parent={DRAWER_ID}
                            label="Recipient Phone" labelUp block onChange={e => phoneSet(e)} />
                    </>
                }

                {
                    sendTo === "wa" &&
                    <PhoneInput id="txt-export-wa" key="txt-export-wa" name="phone" parent={DRAWER_ID}
                        label="Recipient Phone" labelUp block countryCodes="all" onChange={e => phoneSet(e)} />
                }

                {
                    sendTo !== "download" &&
                    <TextArea name="message" block label="Message" labelUp rows={4} marginBottom={0}
                        placeholder="Add a Message (optional)..." onChange={e => messageSet(e.target.value)} />
                }

                <div className="mt-8 d-flex">
                    {
                        sendTo === "download" ?
                            <Button color="success" icon="arrow-down-to-line" className="flex-fill"
                                isRunning={downloading} runningText="Preparing..."
                                text="Download" onClick={download} />
                            :
                            <Button color="success" icon="paper-plane" className="flex-fill"
                                isRunning={sending} runningText="Sending..."
                                text={sendTo === "email" ? "Send Files" : "Send Link"} onClick={send} />
                    }
                </div>

            </div>
        </Drawer>
    );
}
